import React, { useState, SyntheticEvent, useEffect } from 'react';
import styles from './styles/EmbedApp.module.scss';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { Alert } from '@amzn/awsui-components-core';
import { logger } from 'src/logger';

interface IFrameComponentProps {
  src: string;
  title: string;
}

const IFrameComponent: React.FC<IFrameComponentProps> = ({ src, title }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Add timeout for loading
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setHasError(true);
        setErrorMessage('Loading timed out after 30 seconds');
        logger.warn('Iframe loading timed out');
      }
    }, 30000); // 30 second timeout
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  const handleLoad = (event: SyntheticEvent<HTMLIFrameElement, Event>) => {
    setIsLoading(false);
    setHasError(false);
    logger.info('Iframe loaded successfully!');
  };

  const handleError = (event: SyntheticEvent<HTMLIFrameElement, Event>) => {
    setIsLoading(false);
    setHasError(true);
    logger.error('Error loading iframe:', event);
  };

  return (
    <div className={styles.iframeContainer}>
      {isLoading && !hasError && <LoadingSpinner />}
      {hasError && <Alert type="error">{errorMessage || 'Error loading content. Please try again.'}</Alert>}
      <iframe
        src={src}
        title={title}
        onLoad={handleLoad}
        onError={handleError}
        style={{ display: isLoading || hasError ? 'none' : 'block', width: '100%', height: '100%', border: 'none' }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
      />
    </div>
  );
};

export default IFrameComponent;
